import React, { FormEvent, useCallback, useEffect } from 'react'
import { Link, useFetcher, useLoaderData, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { toast } from "react-toastify";
import * as yup from 'yup';
import { setLocale } from 'yup';

// components
import DateComponent from "../components/Date/Date";

// helpers
import { getReservation, updateReservationStep1 } from '../helpers/localStorage';

// interface
import { IreservationStep1 } from '../helpers/interfaces';
import { fetchRequest } from '../helpers/fetcher';
import ButtonInput from '../components/ButtonInput/ButtonInput';
import CheckBoxInput from '../components/CheckBoxInput/CheckBoxInput';

setLocale({
  mixed: {
    required: 'Valore richiesto',
    default: 'Valore non valido',
  },
  string: {
    email: 'Deve essere un indirizzo email valido',
    min: 'Deve essere di almeno ${min} caratteri',
    max: 'Deve essere di massimo ${max} caratteri',
  },
  number: {
    min: 'Deve essere maggiore di ${min}',
    max: 'Deve essere minore di ${min}',
  },
});


export function step1Loader() {
  return getReservation();
}

export async function step1Action({ request }: { request: Request }) {

  const data = await request.formData();
  const { _action, ...values } = Object.fromEntries(data);


  if (_action === "step1") {
    try {

      if (!values.date) throw new Error("Data inserita non valida.");

      updateReservationStep1({
        people: parseInt(values.people as string),
        service: values.service as string,
        date: new Date(values.date as string),
        slot: !!values.slot ? values.slot : undefined,
        status: values.status as 'waiting' | 'pending',
      } as IreservationStep1);

      // return toast.success('Dati salvati correttamente', {
      //   autoClose: 1000,
      //   pauseOnFocusLoss: false,
      //   theme: "colored",
      //   position: "top-center",
      //   onClose: () => window.location.href = "/step2",
      // });

      // const baseUrl = process.env.REACT_APP_BASEURL;
      // return window.location.href = baseUrl + "/step2";

      return true;

    } catch (err) {
      return toast.error((err as Error).message, { theme: "colored", position: "top-center" });
    }
  }
}

type Props = {}

const Step1page: React.FC<Props> = () => {
  const savedReservation = useLoaderData() as any;
  const fetcher = useFetcher();
  const navigate = useNavigate();

  const [validateAll, setValidateAll] = React.useState(false);
  const [reservation, setReservation] = React.useState({
    people: savedReservation.people,
    service: savedReservation.service,
    date: !!savedReservation.date ? new Date(savedReservation.date) : undefined,
    slot: savedReservation.slot,
    status: savedReservation.status,
  });
  const [availability, setAvailability] = React.useState<any>(null);
  
  const fetchAvailability = useCallback(async () => {
    const apiUrl = process.env.REACT_APP_API_FULL_URL;
    const url =
      apiUrl +
      `/customer/reservation-availability/people/${reservation.people}/service/${reservation.service}`;
    try {
      const res = await fetchRequest(
        url,
        "GET",
        "Non è stato possibile caricare la disponibilità del mese. Riprova più tardi."
      );
      setAvailability(res?.data);
      // Process the response data here
    } catch (error) {
      if (error instanceof Error) return window.alert(error.message);
      throw error;
    }
  }, [reservation.people, reservation.service]); // dependencies
  
  useEffect(() => {
    if (fetcher.data && fetcher.data === true) {
      setValidateAll(false);
      navigate("/step2");
    }
  }, [fetcher.data]);


  useEffect(() => {
    if (!!reservation.people && !!reservation.service) fetchAvailability();
  }, [fetchAvailability, reservation.people, reservation.service]); // dependency is now fetchData1

  // Handles
  const setValue = (e: any, type: 'people' | 'service' | 'date' | 'slot') => {
    e.preventDefault();

    let value: any = undefined;
    if (type === 'people') value = parseInt(e.target.innerHTML);
    else if (type === 'service') value = e.target.innerHTML === "Pranzo" ? "lunch" : "dinner";
    else if (type === 'slot') value = e.target.innerHTML;
    else throw new Error("Invalid type");

    setReservation((prevReservation) => {
      return {
        ...prevReservation,
        [type]: value,
      };
    });
  }

  // others
  const isFullDate = (): boolean => {
    if (!availability) return false;
    if (!reservation.date) return false;
    const dateToString = moment(reservation.date).format("YYYY-MM-DD");
    if (availability.full && availability.full.includes(dateToString)) return true;
    return false;
  }

  const step1Schema = yup.object({
    people: yup.number().required().min(1).max(8),
    service: yup.string().required().oneOf(['lunch', 'dinner']),
    date: yup.date().required(),
    slot: yup.string().notRequired()
      .test('slotFullDateTest', 'Scegliere una fascia oraria', (value?: any) => {
        if (isFullDate() === false && !value) return false;
        return true;
      }),
    status: yup.string().oneOf(['waiting', 'pending'])
      .test('statusFullDateTest', 'Accettare la spunta per la lista d\'attesa', (value?: any) => {
        if (isFullDate() === true && value !== 'waiting') return false;
        return true;
      })
  });

  const isSubmitting = fetcher.state === "submitting";

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {

    e.preventDefault();
    setValidateAll(true);

    let formData: any = {
      people: step1Schema.fields.people.cast(reservation.people) as string,
      service: step1Schema.fields.service.cast(reservation.service) as string,
      date: step1Schema.fields.date.cast(reservation.date) as Date,
      status: step1Schema.fields.slot.cast(reservation.status) as string,
    };

    if (reservation.slot) formData.slot = step1Schema.fields.slot.cast(reservation.slot) as string;
    // Validate all fields together
    return step1Schema.validate(formData, { strict: true })
      .then(() => {
        fetcher.submit(
          { _action: 'step1', ...formData },
          { method: 'post' }
        );
      })
      .catch((err: yup.ValidationError) => {
        return toast.error(err.message, { theme: "colored", position: 'bottom-center' });
      });
  };

  return (
    <div className='step-one'>
      <h2>Step 1 - Calendario</h2>

      <fetcher.Form method="post" onSubmit={onSubmit}>

        <input type="hidden" name="_action" value="step1" />

        <div className="data people">
          <label className="label">Numero di persone <span className="required">*</span></label>

          <div className="flexwrapper">
            {/* <input type="hidden" name="people" value={reservation.people} /> */}
            {[1, 2, 3, 4, 5, 6, 7, 8].map((people, index) => (
              <ButtonInput
                key={index}
                name='people'
                label={people.toString()}
                onClick={(e: any) => setValue(e, 'people')}
                value={people}
                selected={reservation.people} />
            ))}
          </div>

          <p>Per numero di persone superiori a 6, contattare direttamente il ristorante al numero <Link className="restaurant-phone" to="tel:+390302751063">0302751063</Link>.</p>
        </div>

        {!!reservation.people && <div className="data service">
          <label className="label">Servizio <span className="required">*</span></label>
          <div className="flexwrapper">
            {/* <input type="hidden" name="service" value={reservation.service} /> */}
            {['lunch', 'dinner'].map((service, index) => (
              <ButtonInput
                key={index}
                name='service'
                label={service === 'lunch' ? 'Pranzo' : 'Cena'}
                onClick={(e: any) => setValue(e, 'service')}
                value={service}
                selected={reservation.service} />
            ))}
          </div>
        </div>}


        {!!reservation.service && <div className="data date">
          <label className="label">Giorno desiderato <span className="required">*</span></label>
          <input type="hidden" name="date" value={!!reservation.date ? reservation.date.toISOString() : ''} />
          <DateComponent
            date={reservation.date}
            availability={availability}
            disabled={reservation.people === undefined || reservation.service === undefined} // not working when inline
            setDay={(date: Date) => {
              setReservation((prevReservation) => {
                return {
                  ...prevReservation,
                  date,
                  slot: undefined,
                  status: 'pending',
                };
              });
            }}
          />
        </div>}

        {reservation.date && isFullDate() === false &&
          <div className="data slot">
            <label className="label">Fascia oraria <span className='required'>*</span></label>
            <div className="flexwrapper">

              {['12:00', '12:30', '13:00', '13:30', '20:00', '20:30', '21:00', '21:30'].map((slot, index) => {

                if (reservation.service === 'dinner' && ['12:00', '12:30', '13:00', '13:30'].includes(slot)) return null;
                if (reservation.service === 'lunch' && ['20:00', '20:30', '21:00', '21:30'].includes(slot)) return null;

                return (
                  <ButtonInput
                    key={index}
                    name='slot'
                    label={slot}
                    onClick={(e: any) => setValue(e, 'slot')}
                    value={slot}
                    selected={reservation.slot}
                    disabled={() => {
                      if (!availability) return true;
                      if (!reservation.date) return true;
                      const dateToString = moment(reservation.date).format("YYYY-MM-DD");
                      if (availability.close && availability.close.includes(dateToString)) return true;
                      if (availability.full && availability.full.includes(dateToString)) return true;
                      if (availability.busy && availability.busy[dateToString] && availability.busy[dateToString][slot] === true) return true;
                      return false;
                    }}
                  />
                )
              })}

            </div>
          </div>
        }

        {isFullDate() === true &&

          <CheckBoxInput
            id='waiting'
            name='waiting'
            label={`Inserisci in LISTA D'ATTESA per il ${moment(reservation.date).format('DD/MM')} a ${reservation.service === 'lunch' ? 'pranzo' : 'cena'}`}
            checked={reservation.status === 'waiting'}
            onChange={(e: React.ChangeEvent<any>) => {
              setReservation((prevReservation) => {
                return {
                  ...prevReservation,
                  status: e.target.checked === true ? 'waiting' : 'pending',
                  slot: undefined,
                };
              });
            }}
            yupSchema={yup.boolean().required().oneOf([true], 'Confermare la messa in lista d\'attesa') as yup.BooleanSchema<boolean>}
            validateNow={validateAll}
            required={true}
          />
        }

        {!!reservation.date && <button type="submit" className="btn btn--dark" disabled={isSubmitting}>
          {isSubmitting ? (
            <span>Salvando...</span>
          ) : (
            <>
              <span>Conferma e Continua</span>
              {/* <CurrencyEuroIcon width={20} /> */}
            </>
          )}
        </button>}

      </fetcher.Form>

      {/* <pre>{JSON.stringify(reservation, null, 2)}</pre> */}

    </div>
  )
}

export default Step1page
